import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { studentApi } from '@/api';
import {
  Button,
  Icon,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Input,
  Textarea,
  useToast,
} from '@/components/ui';
import { errorMessage, fieldErrors } from '@/hooks/useApiError';
import { portfolioProjectSchema } from '@/validations';

const BLANK = { title: '', description: '', project_url: '', repository_url: '' };

/** The API returns nulls; every input on the form wants a string. */
function toForm(project) {
  return Object.fromEntries(Object.keys(BLANK).map((key) => [key, project[key] ?? '']));
}

/**
 * Projects, as distinct from the single portfolio link on the profile form.
 *
 * The link says where the work lives; these say what the work is. A company
 * reading an application wants the second, and one URL cannot carry it. They
 * also complete the same section of the profile as work experience does, which
 * is the point for a student who has not had a job yet.
 */
export function PortfolioProjectsCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});

  const { data: projects = [], isPending } = useQuery({
    queryKey: ['student-portfolio'],
    queryFn: studentApi.listPortfolio,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['student-portfolio'] });
    // A project completes the experience section, so the percentage beside it
    // has to be refetched rather than left showing the old number.
    void queryClient.invalidateQueries({ queryKey: ['student-profile'] });
    void queryClient.invalidateQueries({ queryKey: ['profile-completion'] });
  }

  function close() {
    setOpen(false);
    setEditingId(null);
    setForm(BLANK);
    setErrors({});
  }

  const save = useMutation({
    mutationFn: (input) =>
      editingId ? studentApi.updatePortfolio(editingId, input) : studentApi.addPortfolio(input),
    onSuccess: () => {
      toast.success(editingId ? 'Project updated' : 'Project added');
      refresh();
      close();
    },
    onError: (error) => {
      // Put the server's reasons under the fields they are about; only what
      // cannot be placed there becomes a toast.
      const byField = fieldErrors(error);
      if (Object.keys(byField).length > 0) setErrors(byField);
      else toast.error(errorMessage(error, 'Could not save the project'));
    },
  });

  const remove = useMutation({
    mutationFn: (id) => studentApi.deletePortfolio(id),
    onSuccess: () => {
      toast.success('Project removed');
      refresh();
      setDeleting(null);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the project')),
  });

  function submit() {
    const checked = portfolioProjectSchema.safeParse(form);
    if (!checked.success) {
      setErrors(
        Object.fromEntries(
          checked.error.issues.reverse().map((issue) => [issue.path[0], issue.message]),
        ),
      );
      return;
    }
    setErrors({});
    // An empty box is not a URL, and the backend validates these as real ones.
    save.mutate({
      title: form.title.trim(),
      description: form.description.trim() || null,
      project_url: form.project_url.trim() || null,
      repository_url: form.repository_url.trim() || null,
    });
  }

  function startAdd() {
    setForm(BLANK);
    setErrors({});
    setEditingId(null);
    setOpen(true);
  }

  function startEdit(project) {
    setForm(toForm(project));
    setErrors({});
    setEditingId(project.id);
    setOpen(true);
  }

  function field(name) {
    return {
      value: form[name],
      error: errors[name],
      onChange: (event) => {
        setForm({ ...form, [name]: event.target.value });
        if (errors[name]) setErrors({ ...errors, [name]: undefined });
      },
    };
  }

  return (
    <Card>
      <CardHeader
        title="Projects"
        description="What you have built. Companies see these beside your experience."
        action={
          !open && (
            <Button size="sm" onClick={startAdd}>
              <Icon name="plus" size={15} />
              Add project
            </Button>
          )
        }
      />

      {open && (
        <div className="border-line rounded-input mb-4 space-y-4 border p-4">
          <Input label="Title" required placeholder="Course scheduling app" {...field('title')} />
          <Textarea
            label="What is it?"
            rows={3}
            placeholder="What it does, what you built it with, and which part was yours."
            {...field('description')}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Live project link"
              placeholder="https://myproject.com"
              hint="Where the project can be seen or used."
              {...field('project_url')}
            />
            <Input
              label="Source code link"
              placeholder="https://github.com/you/project"
              hint="GitHub, GitLab or similar."
              {...field('repository_url')}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button size="sm" isLoading={save.isPending} onClick={submit}>
              {editingId ? 'Save project' : 'Add project'}
            </Button>
          </div>
        </div>
      )}

      {isPending ? (
        <p className="text-ink-muted text-sm">Loading…</p>
      ) : projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="A project is often the strongest thing on a student profile."
        />
      ) : (
        <ul className="space-y-3">
          {projects.map((project) => (
            <li key={project.id} className="border-line rounded-input border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 basis-56 items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="bg-brand-50 text-brand-600 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset"
                  >
                    <Icon name="package" size={17} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-ink font-medium break-words">{project.title}</p>
                    {project.description && (
                      <p className="text-ink-muted mt-1 line-clamp-3 text-sm break-words">
                        {project.description}
                      </p>
                    )}
                    {(project.project_url || project.repository_url) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {project.project_url && (
                          <ProjectLink
                            href={project.project_url}
                            icon="external"
                            label="View live project"
                          />
                        )}
                        {project.repository_url && (
                          <ProjectLink
                            href={project.repository_url}
                            icon="github"
                            label="View source code"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Edit ${project.title}`}
                    onClick={() => startEdit(project)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Remove ${project.title}`}
                    onClick={() => setDeleting(project)}
                  >
                    <Icon name="trash" size={14} />
                    Remove
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Remove this project?"
        message={
          deleting ? `"${deleting.title}" is removed from your profile. Nothing else changes.` : ''
        }
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Card>
  );
}

/** A labelled link button. The full address is on hover, not on the button. */
function ProjectLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={href}
      className="border-line bg-surface text-ink hover:border-brand-300 hover:bg-brand-50/60 hover:text-brand-700 rounded-input inline-flex max-w-full min-w-0 items-center gap-1.5 border px-2.5 py-1 text-xs font-medium shadow-xs transition"
    >
      <Icon name={icon} size={14} className="shrink-0" />
      <span>{label}</span>
    </a>
  );
}
